import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

type ExpiredUserRole = 'business-admin' | 'super-admin' | 'end_user' | null;

interface SessionExpiredContextType {
  isSessionExpired: boolean;
  expiredUserRole: ExpiredUserRole;
  triggerSessionExpired: () => void;
  clearSessionExpired: () => void;
}

const SessionExpiredContext = createContext<SessionExpiredContextType | undefined>(undefined);

// Singleton event emitter for 401 events (allows API service to trigger without React context)
type SessionExpiredListener = () => void;
let sessionExpiredListeners: SessionExpiredListener[] = [];

export const subscribeToSessionExpired = (listener: SessionExpiredListener) => {
  sessionExpiredListeners.push(listener);
  return () => {
    sessionExpiredListeners = sessionExpiredListeners.filter(l => l !== listener);
  };
};

export const emitSessionExpired = () => {
  sessionExpiredListeners.forEach(listener => listener());
};

export const SessionExpiredProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [expiredUserRole, setExpiredUserRole] = useState<ExpiredUserRole>(null);

  const triggerSessionExpired = useCallback(() => {
    // Only trigger if not already expired (prevents multiple modals)
    setIsSessionExpired(prev => {
      if (!prev) {
        // Capture the user role before it gets cleared
        try {
          const userData = localStorage.getItem('user_data');
          if (userData) {
            const user = JSON.parse(userData);
            setExpiredUserRole(user.role || null);
          }
        } catch {
          setExpiredUserRole(null);
        }
        return true;
      }
      return prev;
    });
  }, []);

  const clearSessionExpired = useCallback(() => {
    setIsSessionExpired(false);
    setExpiredUserRole(null);
  }, []);

  // Subscribe to global 401 events
  React.useEffect(() => {
    const unsubscribe = subscribeToSessionExpired(triggerSessionExpired);
    return unsubscribe;
  }, [triggerSessionExpired]);

  return (
    <SessionExpiredContext.Provider value={{ 
      isSessionExpired, 
      expiredUserRole,
      triggerSessionExpired, 
      clearSessionExpired 
    }}>
      {children}
    </SessionExpiredContext.Provider>
  );
};

export const useSessionExpired = () => {
  const context = useContext(SessionExpiredContext);
  if (context === undefined) {
    throw new Error('useSessionExpired must be used within a SessionExpiredProvider');
  }
  return context;
};

